import { FC, useEffect, useState } from "react";
import { Button, Typography } from "../common";
import type { CourseCertificateStatus } from "../../store/api/postgresCertificatesApi";
import { useNavigate } from "react-router-dom";

interface LevelCompletionViewProps {
  levelTitle: string;
  levelNumber: number;
  masterCourseId: string;
  masterCourseTitle?: string;
  onContinue: () => void;
  completionKind: "module" | "course";
  certificateStatus?: CourseCertificateStatus;
  onCheckCertificate?: () => void;
  nextLevelTitle?: string;
  /**
   * A follow-on course the learner can start next, e.g. "Leading with AI:
   * Advanced" after finishing "Leading with AI".
   *
   * The CALLER must only pass this when the learner genuinely has access to that
   * course — it comes from the accessible-courses list, not from a constant.
   * Pointing a learner at a course they cannot open is worse than showing no
   * CTA at all. Learners whose package omits Advanced receive no follow-on.
   */
  nextCourse?: { id: string; title: string };
  showCertificateAction?: boolean;
}

export const LevelCompletionView: FC<LevelCompletionViewProps> = ({
  levelTitle,
  levelNumber,
  masterCourseId,
  masterCourseTitle,
  onContinue,
  completionKind,
  certificateStatus = "unknown",
  onCheckCertificate,
  nextLevelTitle,
  nextCourse,
  showCertificateAction = false,
}) => {
  const navigate = useNavigate();
  const isModule = completionKind === "module";
  const [showContent, setShowContent] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  useEffect(() => {
    // Staggered animation timing
    const timer1 = setTimeout(() => setShowContent(true), 300);
    const timer2 = setTimeout(() => setShowStats(true), 600);
    const timer3 = setTimeout(() => setShowButtons(true), 900);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const handleBackToCourse = () => {
    navigate(`/master-courses/${masterCourseId}`);
  };

  const handleCourseComplete = () => {
    navigate("/certificates");
  };

  const handleStartNextCourse = () => {
    if (nextCourse) {navigate(`/master-courses/${nextCourse.id}`);}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-pd-ink via-pd-slate to-pd-ink overflow-y-auto">
      {/* Subtle background accent */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center px-6 py-8 text-center max-w-lg max-h-screen">
        {/* Completion mark */}
        <div
          className={`relative mb-8 transition-all duration-700 ease-out transform ${
            showContent ? "opacity-100 scale-100" : "opacity-0 scale-50"
          }`}
        >
          <svg
            className="w-24 h-24 text-primary"
            viewBox="0 0 96 96"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="48" cy="48" r="46" stroke="currentColor" strokeWidth="2.5" opacity="0.3" />
            <circle cx="48" cy="48" r="38" stroke="currentColor" strokeWidth="2" opacity="0.15" />
            <path
              d="M32 50L44 62L66 36"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Module/Course completed text */}
        <div
          className={`transition-all duration-500 ease-out transform ${
            showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <Typography
            variant="h2"
            weight="bold"
            color="none"
            className="text-white mb-2 !text-3xl md:!text-4xl"
          >
            {isModule ? "Module Completed" : "Course Completed"}
          </Typography>
          <Typography
            variant="h4"
            color="none"
            className="text-primary mb-6 !text-xl md:!text-2xl"
          >
            {isModule ? levelTitle : masterCourseTitle || levelTitle}
          </Typography>
        </div>

        {/* Stats card */}
        <div
          className={`w-full transition-all duration-500 ease-out transform ${
            showStats ? "opacity-100 scale-100" : "opacity-0 scale-90"
          }`}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-white/20">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="text-left">
                <Typography variant="small" color="none" className="text-white/70">
                  Module
                </Typography>
                <Typography variant="h4" weight="bold" color="none" className="text-white">
                  {levelNumber}
                </Typography>
              </div>
            </div>

            {masterCourseTitle && (
              <Typography variant="small" color="none" className="text-white/60">
                {masterCourseTitle}
              </Typography>
            )}
          </div>
        </div>

        {!isModule && <div role="status" className="text-white mb-4">
          {certificateStatus === "ready" ? "Your course certificate is ready."
            : certificateStatus === "generating" ? "Your course certificate is being generated."
              : certificateStatus === "checking" ? "Checking your course certificate…"
                : certificateStatus === "failed" ? "Your course is complete, but certificate generation failed."
                  : certificateStatus === "unavailable" ? "Your course is complete. A certificate is not available for this course yet."
                    : certificateStatus === "not-entitled" ? "Your course is complete. Certificates are not included in your current access."
                      : "Your course is complete. We couldn't confirm its certificate status yet."}
          {onCheckCertificate && !["ready", "checking", "not-entitled"].includes(certificateStatus) &&
            <button className="block mx-auto underline mt-2" onClick={onCheckCertificate}>Check certificate status</button>}
        </div>}

        {/* Action buttons */}
        <div
          className={`w-full flex flex-col gap-3 transition-all duration-500 ease-out transform ${
            showButtons ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {isModule ? (
            <>
              {nextLevelTitle && <Button
                variant="filled"
                size="large"
                className="w-full !py-4 !text-lg font-semibold"
                onClick={onContinue}
              >
                {`Continue to ${nextLevelTitle}`}
              </Button>}
              <button
                className="w-full py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors"
                onClick={handleBackToCourse}
              >
                Back to Course
              </button>
            </>
          ) : (
            <>
              {nextCourse ? (
                <>
                  <Button
                    variant="filled"
                    size="large"
                    className="w-full !py-4 !text-lg font-semibold"
                    onClick={handleStartNextCourse}
                  >
                    {`Start ${nextCourse.title}`}
                  </Button>
                  {showCertificateAction && (
                    <button
                      className="w-full py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors"
                      onClick={handleCourseComplete}
                    >
                      View My Certificates
                    </button>
                  )}
                </>
              ) : showCertificateAction ? (
                <Button
                  variant="filled"
                  size="large"
                  className="w-full !py-4 !text-lg font-semibold"
                  onClick={handleCourseComplete}
                >
                  View My Certificates
                </Button>
              ) : null}
              <button
                className="w-full py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors"
                onClick={handleBackToCourse}
              >
                Review This Course
              </button>
            </>
          )}
        </div>

        {/* Completion message */}
        <div
          className={`mt-6 transition-all duration-500 delay-300 ${
            showButtons ? "opacity-100" : "opacity-0"
          }`}
        >
          <Typography variant="small" color="none" className="text-white/50">
            {isModule
              ? nextLevelTitle ? "Continue with your remaining modules." : "Return to your course to review your progress."
              : "You have successfully completed this course."}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default LevelCompletionView;
